import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, CircularProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import StarRateIcon from "@mui/icons-material/StarRate";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import useBusinessQuery from "../../store/Bussiness/useBusinessQuery";

/* ── helpers ──────────────────────────────────────────────────────────────── */
const fmt = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return d; }
};

const parseFeatures = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
};

/* ── status config ────────────────────────────────────────────────────────── */
const STATUS = {
  active:  { label: "Active",  bg: "#ecfdf5", color: "#10b981", Icon: CheckCircleOutlinedIcon },
  pending: { label: "Pending", bg: "#fff8e6", color: "#f59e0b", Icon: PendingOutlinedIcon },
  expired: { label: "Expired", bg: "#fff0f0", color: "#e53935", Icon: ErrorOutlineIcon },
};

const getStatusKey = (plan) => {
  if (!plan) return null;
  const s = (plan?.status || "").toLowerCase();
  if (s === "active" || plan?.is_active === true) return "active";
  if (s === "pending") return "pending";
  return "expired";
};

/* ── colour palette per plan index ───────────────────────────────────────── */
const CARD_ACCENTS = [
  { border: "#bfdbfe", badge: "#2182B1", badgeText: "#fff", btn: "linear-gradient(135deg,#1d6fa4,#2182B1)" },
  { border: "#bbf7d0", badge: "#059669", badgeText: "#fff", btn: "linear-gradient(135deg,#047857,#059669)" },
  { border: "#fde68a", badge: "#d97706", badgeText: "#fff", btn: "linear-gradient(135deg,#b45309,#d97706)" },
  { border: "#e9d5ff", badge: "#7c3aed", badgeText: "#fff", btn: "linear-gradient(135deg,#6d28d9,#7c3aed)" },
  { border: "#fecaca", badge: "#dc2626", badgeText: "#fff", btn: "linear-gradient(135deg,#b91c1c,#dc2626)" },
  { border: "#c7d2fe", badge: "#4f46e5", badgeText: "#fff", btn: "linear-gradient(135deg,#4338ca,#4f46e5)" },
];

/* ── sub-components ───────────────────────────────────────────────────────── */

const StatusPill = ({ statusKey, small }) => {
  const cfg = STATUS[statusKey];
  if (!cfg) return null;
  const { Icon } = cfg;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: cfg.bg, borderRadius: 20,
      padding: small ? "3px 10px" : "5px 14px",
      flexShrink: 0,
    }}>
      <Icon sx={{ fontSize: small ? 12 : 14, color: cfg.color }} />
      <span style={{ fontSize: small ? 11 : 12, fontWeight: 700, color: cfg.color }}>
        {cfg.label}
      </span>
    </span>
  );
};

/* current-plan summary card */
const CurrentPlanCard = ({ plan, statusKey }) => {
  const planName     = plan?.plan_name || plan?.name || plan?.subscription_plan?.name || "—";
  const price        = plan?.price || plan?.subscription_plan?.price;
  const validity     = plan?.validity_days || plan?.subscription_plan?.validity_days;
  const startDate    = plan?.start_date || plan?.created_at || null;
  const endDate      = plan?.end_date || plan?.expiry_date || plan?.expire_date || null;
  const remaining    = plan?.remaining_days ?? plan?.balance_days ?? null;
  const subId        = plan?.id || plan?.subscription_id || null;
  const local        = JSON.parse(localStorage.getItem("user") || "{}");
  const customerId   = local?.id || local?.user_id || null;
  const features     = parseFeatures(plan?.features || plan?.subscription_plan?.features);

  const remainColor  = remaining > 10 ? "#10b981" : remaining > 0 ? "#f59e0b" : "#e53935";
  const remainBg     = remaining > 10 ? "#ecfdf5" : remaining > 0 ? "#fef9ec" : "#fff0f0";
  const remainBorder = remaining > 10 ? "#a7f3d0" : remaining > 0 ? "#fcd34d" : "#fca5a5";
  const remainMsg    = remaining > 10 ? "Subscription is active"
                     : remaining > 0  ? "Expiring soon!"
                     : "Subscription has expired";

  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      boxShadow: "0 2px 16px rgba(14,58,83,0.10)",
      border: "1.5px solid #e8f0f5",
      overflow: "hidden",
      marginBottom: 20,
    }}>
      {/* card header */}
      <div style={{
        background: "linear-gradient(135deg,#0E3A53,#1a6b8a)",
        padding: "16px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 12,
            background: "rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <WorkspacePremiumOutlinedIcon sx={{ fontSize: 20, color: "#fbbf24" }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 11, color: "rgba(255,255,255,0.65)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Current Plan
            </p>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#fff" }}>
              {planName}
            </p>
          </div>
        </div>
        <StatusPill statusKey={statusKey} />
      </div>

      {/* info rows */}
      <div style={{ padding: "4px 20px 0" }}>
        {[
          { Icon: CalendarTodayOutlinedIcon, label: "Start Date",    value: fmt(startDate) },
          { Icon: CalendarTodayOutlinedIcon, label: "Expiry Date",   value: fmt(endDate) },
          { Icon: AccessTimeOutlinedIcon,    label: "Remaining",     value: remaining !== null ? `${remaining} Days` : "—", valueColor: remainColor },
          ...(customerId ? [{ Icon: BadgeOutlinedIcon,                label: "Customer ID",   value: `#${customerId}` }] : []),
          ...(subId      ? [{ Icon: ConfirmationNumberOutlinedIcon,   label: "Subscription ID", value: `#${subId}` }] : []),
        ].map(({ Icon, label, value, valueColor }, i, arr) => (
          <div key={label} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "11px 0",
            borderBottom: i < arr.length - 1 ? "1px solid #f0f4f7" : "none",
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 10,
              background: "#f0f7fb",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Icon sx={{ fontSize: 16, color: "#2182B1" }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 10, color: "#9ab0be", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {label}
              </p>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: valueColor || "#1a2e3b", marginTop: 1 }}>
                {value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* remaining days bar */}
      {remaining !== null && (
        <div style={{
          margin: "12px 20px 16px",
          background: remainBg,
          border: `1.5px solid ${remainBorder}`,
          borderRadius: 12,
          padding: "12px 16px",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <HourglassBottomOutlinedIcon sx={{ fontSize: 24, color: remainColor, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: remainColor, lineHeight: 1 }}>
              {remaining} Days
            </p>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: remainColor, marginTop: 2 }}>
              {remainMsg}
            </p>
          </div>
        </div>
      )}

      {/* features */}
      {features.length > 0 && (
        <div style={{ padding: "0 20px 16px" }}>
          <p style={{ margin: "0 0 10px", fontSize: 11, color: "#9ab0be", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Plan Benefits
          </p>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "6px 12px",
          }}>
            {features.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: 6,
                  background: "#ecfdf5",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, marginTop: 1,
                }}>
                  <CheckIcon sx={{ fontSize: 11, color: "#10b981" }} />
                </div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: "#374151", lineHeight: 1.5 }}>
                  {f}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* available plan card */
const PlanCard = ({ item, isCurrentPlan, statusKey, accent }) => {
  const features  = parseFeatures(item?.features);
  const isExpired = statusKey === "expired";
  const ac        = accent || CARD_ACCENTS[0];

  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      border: isCurrentPlan ? "2px solid #2182B1" : `1.5px solid ${ac.border}`,
      boxShadow: isCurrentPlan
        ? "0 4px 20px rgba(33,130,177,0.18)"
        : "0 2px 10px rgba(14,58,83,0.07)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      position: "relative",
    }}>
      {/* top accent strip */}
      <div style={{
        height: 4,
        background: isCurrentPlan ? "linear-gradient(90deg,#0E3A53,#2182B1)" : ac.btn,
      }} />

      {/* current plan badge */}
      {isCurrentPlan && (
        <div style={{
          position: "absolute", top: 12, right: 12,
          background: "#fbbf24", borderRadius: 6,
          padding: "3px 9px",
          fontSize: 10, fontWeight: 800, color: "#78350f", letterSpacing: 0.4,
        }}>
          CURRENT PLAN
        </div>
      )}

      <div style={{ padding: "16px 18px 18px", flex: 1, display: "flex", flexDirection: "column" }}>
        {/* plan name */}
        <p style={{
          margin: "0 0 6px",
          fontSize: 15, fontWeight: 800,
          color: "#0E3A53",
          paddingRight: isCurrentPlan ? 90 : 0,
        }}>
          {item?.name || "—"}
        </p>

        {/* price */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#2182B1" }}>₹</span>
          <span style={{ fontSize: 24, fontWeight: 900, color: "#2182B1", lineHeight: 1 }}>
            {item?.price ?? "—"}
          </span>
          {item?.validity_days && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "#9ab0be" }}>
              / {item.validity_days} days
            </span>
          )}
        </div>

        {/* description */}
        {item?.description && (
          <p style={{ margin: "0 0 12px", fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
            {item.description}
          </p>
        )}

        {/* features */}
        {features.length > 0 && (
          <div style={{ flex: 1, marginBottom: 16 }}>
            {features.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: 5,
                  background: isCurrentPlan ? "#dbeafe" : "#f0fdf4",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, marginTop: 1,
                }}>
                  <CheckIcon sx={{ fontSize: 11, color: isCurrentPlan ? "#2182B1" : "#10b981" }} />
                </div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: "#374151", lineHeight: 1.5 }}>
                  {f}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* action button */}
        <Button
          variant="contained"
          startIcon={<AutorenewIcon sx={{ fontSize: 14 }} />}
          style={{
            background: isCurrentPlan
              ? (isExpired ? "#e53935" : "linear-gradient(135deg,#0E3A53,#1a6b8a)")
              : ac.btn,
            color: "#fff",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            padding: "9px 16px",
            textTransform: "none",
            boxShadow: "none",
            width: "100%",
            marginTop: "auto",
          }}
        >
          {isCurrentPlan
            ? (isExpired ? "Renew Plan" : "Upgrade / Renew")
            : "Upgrade to this Plan"}
        </Button>
      </div>
    </div>
  );
};

/* ── main page ────────────────────────────────────────────────────────────── */
const MySubscription = () => {
  const history = useHistory();
  const [businessId, setBusinessId] = useState(null);

  const { data: bizList, isSuccess: bizSuccess } = useBusinessQuery.Get_my_bussiness();
  const { data: planData,  isLoading: planLoading  } = useBusinessQuery.Get_my_currentplan(businessId);
  const { data: plansData, isLoading: plansLoading } = useBusinessQuery.Get_subcription(businessId);

  useEffect(() => {
    if (bizSuccess && bizList?.data?.results?.length > 0) {
      setBusinessId(bizList.data.results[0].id);
    }
  }, [bizSuccess, bizList]);

  const plan           = planData?.data || planData;
  const statusKey      = getStatusKey(plan);
  const planName       = plan?.plan_name || plan?.name || plan?.subscription_plan?.name || "—";
  const currentPlanId  = plan?.plan_id || plan?.subscription_plan?.id || null;
  const availablePlans = plansData?.data?.results || plansData?.results || plansData?.data || [];

  return (
    <div style={{ minHeight: "100vh", background: "#f4f7fb" }}>

      {/* ── top bar ── */}
      <div style={{
        background: "linear-gradient(135deg,#0E3A53 0%,#1a6b8a 100%)",
        padding: "0 16px",
      }}>
        {/* nav row */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 0 20px",
        }}>
          <button
            onClick={() => history.push("/my-profile")}
            style={{
              background: "rgba(255,255,255,0.15)", border: "none",
              borderRadius: 10, width: 36, height: 36,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 20, color: "#fff" }} />
          </button>
          <div>
            <p style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#fff" }}>
              Subscription Plans
            </p>
            <p style={{ margin: 0, fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 1 }}>
              Choose the plan that best fits your needs
            </p>
          </div>
        </div>

        {/* current plan hero pill */}
        {plan && (
          <div style={{
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.22)",
            borderRadius: 14,
            padding: "12px 16px",
            display: "flex", alignItems: "center", gap: 12,
            marginBottom: 20,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <StarRateIcon sx={{ fontSize: 22, color: "#fbbf24" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 11, color: "rgba(255,255,255,0.65)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                Active Plan
              </p>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {planName}
              </p>
            </div>
            {statusKey && <StatusPill statusKey={statusKey} small />}
          </div>
        )}
      </div>

      {/* ── body ── */}
      <div style={{ padding: "20px 16px 40px" }}>

        {/* current plan details */}
        {planLoading ? (
          <div style={{
            background: "#fff", borderRadius: 16, padding: 40,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 12px rgba(14,58,83,0.08)", marginBottom: 20,
          }}>
            <CircularProgress size={30} sx={{ color: "#2182B1" }} />
          </div>
        ) : !plan ? (
          <div style={{
            background: "#fff", borderRadius: 16, padding: "28px 20px",
            textAlign: "center",
            boxShadow: "0 2px 12px rgba(14,58,83,0.08)", marginBottom: 20,
          }}>
            <ErrorOutlineIcon sx={{ fontSize: 38, color: "#c5d8e3" }} />
            <p style={{ margin: "8px 0 4px", fontSize: 14, fontWeight: 600, color: "#9ab0be" }}>
              No active subscription found
            </p>
            <p style={{ margin: 0, fontSize: 12, color: "#b8cdd8" }}>
              Choose a plan below to get started
            </p>
          </div>
        ) : (
          <CurrentPlanCard plan={plan} statusKey={statusKey} />
        )}

        {/* available plans section */}
        <div style={{ marginBottom: 12 }}>
          <p style={{
            margin: "0 0 4px",
            fontSize: 16, fontWeight: 800, color: "#0E3A53",
          }}>
            Available Subscription Plans
          </p>
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b" }}>
            Select a plan to upgrade or renew your subscription
          </p>
        </div>

        {plansLoading ? (
          <div style={{
            background: "#fff", borderRadius: 14, padding: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 10px rgba(14,58,83,0.07)",
          }}>
            <CircularProgress size={28} sx={{ color: "#2182B1" }} />
          </div>
        ) : availablePlans.length === 0 ? (
          <div style={{
            background: "#fff", borderRadius: 14, padding: "24px 20px",
            textAlign: "center",
            boxShadow: "0 2px 10px rgba(14,58,83,0.07)",
          }}>
            <Typography style={{ fontSize: 13, color: "#9ab0be", fontWeight: 600 }}>
              No plans available at the moment
            </Typography>
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16,
          }}>
            {availablePlans.map((item, idx) => (
              <PlanCard
                key={item.id}
                item={item}
                isCurrentPlan={!!(currentPlanId && item.id === currentPlanId)}
                statusKey={statusKey}
                accent={CARD_ACCENTS[idx % CARD_ACCENTS.length]}
              />
            ))}
          </div>
        )}

        {/* help footer */}
        <div style={{
          marginTop: 28,
          background: "#fff",
          borderRadius: 14,
          border: "1.5px solid #e8f0f5",
          padding: "16px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12,
          boxShadow: "0 2px 10px rgba(14,58,83,0.06)",
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0E3A53" }}>
              Need help choosing a plan?
            </p>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>
              Our team is ready to assist you
            </p>
          </div>
          <a
            href="https://wa.me/917293838383?text=Hello"
            style={{ textDecoration: "none" }}
          >
            <Button
              variant="contained"
              style={{
                background: "linear-gradient(135deg,#25d366,#128c7e)",
                color: "#fff",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                padding: "8px 18px",
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              WhatsApp Us
            </Button>
          </a>
        </div>

      </div>
    </div>
  );
};

export default MySubscription;
